import { MinusCircleOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, InputNumber, message, Select, Space, Upload } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ContentWithErrorHandler from '../../app/ContentWithErrorHandler';
import { useGetAllMemberQuery } from '../member/membersAPI';
import { useGetAllUmkmCategoriesQuery } from './umkmCategoryAPI';
import { useCreateUmkmMutation, useGetUmkmQuery, useUpdateUmkmMutation } from './umkmAPI';

const { TextArea } = Input;
const { Option } = Select;

function UmkmDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [productFileList, setProductFileList] = useState<Record<number, UploadFile[]>>({});

  const { data, error, isLoading } = useGetUmkmQuery(id!, { skip: !id });
  const { data: categories } = useGetAllUmkmCategoriesQuery();
  const { data: members } = useGetAllMemberQuery(true);
  const [createUmkm] = useCreateUmkmMutation();
  const [updateUmkm] = useUpdateUmkmMutation();

  useEffect(() => {
    if (data) {
      form.setFieldsValue({
        name: data.name,
        address: data.address,
        description: data.description,
        whatsappNumber: data.whatsappNumber,
        ownerId: data.ownerId,
        categoryIds: data.categories?.map((c) => c.id),
        products: data.products || [],
      });
      if (data.images && data.images.length > 0) {
        setFileList(
          data.images.map((img, idx) => ({
            uid: `-${idx}`,
            name: `image-${idx}`,
            status: 'done',
            url: img,
          })),
        );
      }
      // Load product images
      if (data.products && data.products.length > 0) {
        const productImages: Record<number, UploadFile[]> = {};
        data.products.forEach((product: any, productIdx: number) => {
          if (product.images && Array.isArray(product.images) && product.images.length > 0) {
            productImages[productIdx] = product.images.map((img, imgIdx) => ({
              uid: `product-${productIdx}-${imgIdx}`,
              name: `product-image-${productIdx}-${imgIdx}`,
              status: 'done',
              url: img,
            }));
          }
        });
        setProductFileList(productImages);
      }
    }
  }, [data, form]);

  const onFinish = async (values: any) => {
    try {
      const formData = new FormData();
      formData.append('name', values.name);
      if (values.address) formData.append('address', values.address);
      if (values.description) formData.append('description', values.description);
      if (values.whatsappNumber) formData.append('whatsappNumber', values.whatsappNumber);
      if (values.ownerId) formData.append('ownerId', values.ownerId.toString());
      if (values.categoryIds) formData.append('categoryIds', JSON.stringify(values.categoryIds));

      const products = (values.products || []).map((product: any, idx: number) => {
        const existingImages = data?.products?.[idx]?.images || [];
        return {
          ...product,
          images: product.images || existingImages,
        };
      });
      if (products.length > 0) {
        formData.append('products', JSON.stringify(products));
      }

      // Handle UMKM images if any
      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append('files', file.originFileObj);
        }
      });

      Object.entries(productFileList).forEach(([idxStr, files]) => {
        files.forEach((file) => {
          if (file.originFileObj) {
            formData.append(`productImages_${idxStr}`, file.originFileObj);
          }
        });
      });

      if (id) {
        formData.append('id', id);
        await updateUmkm(formData).unwrap();
        message.success('UMKM berhasil diupdate');
      } else {
        await createUmkm(formData).unwrap();
        message.success('UMKM berhasil dibuat');
      }
      navigate('/umkm');
    } catch (error) {
      message.error('Gagal menyimpan UMKM');
    }
  };

  return (
    <ContentWithErrorHandler isError={error} isLoading={isLoading}>
      <Card title={id ? 'Edit UMKM' : 'Tambah UMKM'} size="small">
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item label="Nama UMKM" name="name" rules={[{ required: true, message: 'Nama UMKM harus diisi' }]}>
            <Input />
          </Form.Item>

          <Form.Item label="Pemilik" name="ownerId">
            <Select showSearch placeholder="Pilih pemilik" optionFilterProp="children">
              {members?.map((member: any) => (
                <Option key={member.id} value={member.id}>
                  {member.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Kategori" name="categoryIds">
            <Select mode="multiple" placeholder="Pilih kategori">
              {categories?.map((cat) => (
                <Option key={cat.id} value={cat.id}>
                  {cat.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label="Alamat" name="address">
            <TextArea rows={2} />
          </Form.Item>

          <Form.Item label="Deskripsi" name="description">
            <TextArea rows={4} />
          </Form.Item>

          <Form.Item label="Nomor WhatsApp" name="whatsappNumber">
            <Input placeholder="628123456789" />
          </Form.Item>

          <Form.Item label="Gambar UMKM">
            <Upload listType="picture" fileList={fileList} beforeUpload={() => false} onChange={({ fileList }) => setFileList(fileList)} multiple>
              <Button icon={<UploadOutlined />}>Upload Gambar</Button>
            </Upload>
          </Form.Item>

          <Card title="Produk" size="small" style={{ marginBottom: 16 }}>
            <Form.List name="products">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Card
                      key={key}
                      size="small"
                      style={{ marginBottom: 16 }}
                      title={`Produk #${name + 1}`}
                      extra={<MinusCircleOutlined onClick={() => remove(name)} style={{ cursor: 'pointer', color: 'red' }} />}
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <Form.Item {...restField} name={[name, 'name']} rules={[{ required: true, message: 'Nama produk harus diisi' }]}>
                          <Input placeholder="Nama Produk" />
                        </Form.Item>
                        <Form.Item {...restField} name={[name, 'description']}>
                          <Input.TextArea placeholder="Deskripsi" rows={2} />
                        </Form.Item>
                        <Form.Item {...restField} name={[name, 'price']}>
                          <InputNumber
                            placeholder="Harga"
                            style={{ width: '100%' }}
                            formatter={(value) => `Rp ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={(value: any) => value.replace(/Rp\s?|(,*)/g, '')}
                          />
                        </Form.Item>
                        <Form.Item label="Gambar Produk">
                          <Upload
                            listType="picture"
                            fileList={productFileList[name] || []}
                            beforeUpload={() => false}
                            onChange={({ fileList }) => setProductFileList({ ...productFileList, [name]: fileList })}
                            multiple
                          >
                            <Button icon={<UploadOutlined />}>Upload Gambar</Button>
                          </Upload>
                        </Form.Item>
                      </Space>
                    </Card>
                  ))}
                  <Form.Item>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                      Tambah Produk
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </Card>

          <Form.Item>
            <Button type="primary" htmlType="submit" style={{ marginRight: 8 }}>
              Simpan
            </Button>
            <Button onClick={() => navigate('/umkm')}>Batal</Button>
          </Form.Item>
        </Form>
      </Card>
    </ContentWithErrorHandler>
  );
}

export default UmkmDetail;
